import { useEffect, useRef } from 'react';

import { createBrowserRouter, Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';

import { CannyProvider } from 'components/Canny/CannyProvider';
import { Seo } from 'components/Seo';
import { ZendeskProvider } from 'components/Zendesk';
import { Modals, InjectCouponCode } from 'modules/common/components';
import { paths } from 'paths';
import { prefetchAuthenticatedRoutes } from 'routePrefetch';
import { type CampaignParams, resolveCampaignParams, stripCampaignParamsFromReturnTo } from 'utils/campaign';
import { createLazyImport, createLazyPageImport } from 'utils/lazy';
import { PersistService } from 'utils/persist';

// #region Layouts
const AccountLayout = createLazyImport(() => import('modules/accounts/layouts'), 'AccountLayout');
const AuthLayout = createLazyImport(() => import('modules/auth/layouts'), 'AuthLayout');
const AppLayout = createLazyImport(() => import('modules/common/layouts'), 'AppLayout');
const BaseLayout = createLazyImport(() => import('modules/common/layouts'), 'BaseLayout');
const OrderLayout = createLazyImport(() => import('modules/orders/layouts'), 'OrderLayout');
// #endregion Layouts

// #region Guards
const AuthGuard = createLazyImport(() => import('modules/common/guards'), 'AuthGuard');
const CredentialsGeneratorGuard = createLazyImport(() => import('modules/proxies/guards'), 'CredentialsGeneratorGuard');
const FeatureFlagGuard = createLazyImport(() => import('modules/common/guards'), 'FeatureFlagGuard');
const GuestGuard = createLazyImport(() => import('modules/common/guards'), 'GuestGuard');
const OAuthGuard = createLazyImport(() => import('modules/common/guards'), 'OAuthGuard');
const OrderStepGuard = createLazyImport(() => import('modules/orders/guards'), 'OrderStepGuard');
const OrganizationAdminGuard = createLazyImport(() => import('modules/organizations/guards'), 'OrganizationAdminGuard');
const OrganizationPermissionGuard = createLazyImport(
  () => import('modules/organizations/guards'),
  'OrganizationPermissionGuard',
);
const ReplaceableProxiesGuard = createLazyImport(() => import('modules/common/guards'), 'ReplaceableProxiesGuard');
// #endregion Guards

// #region Pages
const AccountGeneralPage = createLazyPageImport(() => import('modules/accounts/pages'), 'AccountGeneralPage');
const AccountIdentityVerificationPage = createLazyPageImport(
  () => import('modules/accounts/pages'),
  'AccountIdentityVerificationPage',
);
const AccountNotificationsPage = createLazyPageImport(
  () => import('modules/accounts/pages'),
  'AccountNotificationsPage',
);
const AccountPaymentsPage = createLazyPageImport(() => import('modules/accounts/pages'), 'AccountPaymentsPage');
const AccountSecurityPage = createLazyPageImport(() => import('modules/accounts/pages'), 'AccountSecurityPage');
const APIKeysPage = createLazyPageImport(() => import('modules/api-keys/pages'), 'APIKeysPage');
const ForgotPasswordPage = createLazyPageImport(() => import('modules/auth/pages'), 'ForgotPasswordPage');
const ImpersonateUserPage = createLazyPageImport(() => import('modules/auth/pages'), 'ImpersonateUserPage');
const MultiFactorAuthPage = createLazyPageImport(() => import('modules/auth/pages'), 'MultiFactorAuthPage');
const ResetPasswordPage = createLazyPageImport(() => import('modules/auth/pages'), 'ResetPasswordPage');
const SignInPage = createLazyPageImport(() => import('modules/auth/pages'), 'SignInPage');
const SignUpPage = createLazyPageImport(() => import('modules/auth/pages'), 'SignUpPage');
const PaymentsCallbackPage = createLazyPageImport(() => import('modules/billing/pages'), 'PaymentsCallbackPage');
const NotFoundPage = createLazyPageImport(() => import('modules/common/pages'), 'NotFoundPage');
const OverviewPage = createLazyPageImport(() => import('modules/dashboard/pages'), 'OverviewPage');
const FeatureRequestPage = createLazyPageImport(() => import('modules/featureRequest/pages'), 'FeatureRequestPage');
const MarketplacePage = createLazyPageImport(() => import('modules/marketplace/pages'), 'MarketplacePage');
const OrderCheckoutPage = createLazyPageImport(() => import('modules/orders/pages'), 'OrderCheckoutPage');
const OrderDetailsStepPage = createLazyPageImport(() => import('modules/orders/pages'), 'OrderDetailsStepPage');
const OrderProxyPage = createLazyPageImport(() => import('modules/orders/pages'), 'OrderProxyPage');
const OrderSetupPage = createLazyPageImport(() => import('modules/orders/pages'), 'OrderSetupPage');
const CredentialsGeneratorPage = createLazyPageImport(
  () => import('modules/proxies/pages'),
  'CredentialsGeneratorPage',
);
const MyProxiesPage = createLazyPageImport(() => import('modules/proxies/pages'), 'MyProxiesPage');
const OperationPage = createLazyPageImport(() => import('modules/proxies/pages'), 'OperationPage');
const ProxyOverviewPage = createLazyPageImport(() => import('modules/proxies/pages'), 'ProxyOverviewPage');
const ReplaceProxiesPage = createLazyPageImport(() => import('modules/proxies/pages'), 'ReplaceProxiesPage');
const BankDetailsPage = createLazyImport(() => import('modules/referrals/pages'), 'BankDetailsPage');
const ReferralsPage = createLazyImport(() => import('modules/referrals/pages'), 'ReferralsPage');
const ResellerPage = createLazyPageImport(() => import('modules/resellers/pages'), 'ResellerPage');
const OrganizationCreatePage = createLazyPageImport(
  () => import('modules/organizations/pages'),
  'OrganizationCreatePage',
);
const OrganizationManagePage = createLazyPageImport(
  () => import('modules/organizations/pages'),
  'OrganizationManagePage',
);
const WebhooksPage = createLazyPageImport(() => import('modules/webhooks/pages'), 'WebhooksPage');
const WebhookDetailsPage = createLazyPageImport(() => import('modules/webhooks/pages'), 'WebhookDetailsPage');
// #endregion Pages

prefetchAuthenticatedRoutes();

function isAuthEntryPath(pathname: string) {
  return (
    [
      paths.auth.signIn,
      paths.auth.mfa,
      paths.auth.signUp,
      paths.auth.forgotPassword,
      paths.auth.resetPassword,
    ].includes(pathname) || pathname.startsWith(`${paths.auth.resetPassword}/`)
  );
}

export function RootLayout() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const lastSearchRef = useRef<string | null>(null);
  const currentSearch = searchParams.toString();
  const justSignedOut = PersistService.get<boolean>('auth.justSignedOut') === true;

  // Capture synchronously at the route boundary so coupon injection and auth guards
  // cannot remove or wrap the original campaign values first.
  if (lastSearchRef.current !== currentSearch) {
    lastSearchRef.current = currentSearch;

    const storedParams = PersistService.get<CampaignParams>('campaignParams');
    const campaignParams = resolveCampaignParams(searchParams, storedParams ?? undefined);

    if (Object.keys(campaignParams).length > 0) {
      PersistService.set('campaignParams', campaignParams);
    }
  }

  useEffect(() => {
    if (!justSignedOut || !isAuthEntryPath(pathname)) return;

    const returnTo = searchParams.get('returnTo');
    const sanitizedReturnTo = stripCampaignParamsFromReturnTo(returnTo);

    if (returnTo && sanitizedReturnTo && sanitizedReturnTo !== returnTo) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('returnTo', sanitizedReturnTo);
      setSearchParams(nextSearchParams, { replace: true });

      return;
    }

    PersistService.remove('auth.justSignedOut');
  }, [justSignedOut, pathname, searchParams, setSearchParams]);

  return (
    <ZendeskProvider>
      <Seo />
      <Modals.Provider>
        <InjectCouponCode>
          <Outlet />
        </InjectCouponCode>
      </Modals.Provider>
    </ZendeskProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: paths.index,
        element: (
          <AuthGuard>
            <AppLayout />
          </AuthGuard>
        ),
        children: [
          { index: true, element: <OverviewPage /> },
          { path: paths.apiKeys.index, element: <APIKeysPage /> },
          { path: paths.organizations.create, element: <OrganizationCreatePage /> },
          {
            path: paths.organizations.manage(),
            element: (
              <OrganizationAdminGuard>
                <OrganizationManagePage />
              </OrganizationAdminGuard>
            ),
          },
          { path: paths.referrals.index, element: <ReferralsPage /> },
          { path: paths.reseller.index, element: <ResellerPage /> },

          {
            path: paths.webhooks.index,
            children: [
              { index: true, element: <WebhooksPage /> },
              { path: paths.webhooks.details(), element: <WebhookDetailsPage /> },
            ],
          },

          {
            path: paths.proxies.index,
            children: [
              { index: true, element: <MyProxiesPage /> },
              { path: `${paths.proxies.index}/:proxyId`, element: <ProxyOverviewPage /> },
            ],
          },
          {
            path: paths.account.index,
            element: <AccountLayout />,
            children: [
              { index: true, element: <AccountGeneralPage /> },
              { path: paths.account.general, element: <AccountGeneralPage /> },
              { path: paths.account.notifications, element: <AccountNotificationsPage /> },
              { path: paths.account.security, element: <AccountSecurityPage /> },
              { path: paths.account.identityVerification, element: <AccountIdentityVerificationPage /> },
              { path: paths.account.payments, element: <AccountPaymentsPage /> },
            ],
          },
          {
            path: paths.marketplace.index,
            element: (
              <FeatureFlagGuard name="isDealsInventoryEnabled">
                <MarketplacePage />
              </FeatureFlagGuard>
            ),
          },
        ],
      },
      { path: paths.auth.logout, element: <div /> },
      { path: `${paths.auth.switch}/:email`, element: <ImpersonateUserPage /> },
      {
        path: paths.order.index,
        element: (
          <AuthGuard>
            <OrganizationPermissionGuard permission="order_purchase">
              <OrderStepGuard>
                <OrderLayout />
              </OrderStepGuard>
            </OrganizationPermissionGuard>
          </AuthGuard>
        ),
        children: [
          { index: true, element: <OrderSetupPage /> },
          { path: paths.order.proxy, element: <OrderProxyPage /> },
          { path: paths.order.details, element: <OrderDetailsStepPage /> },
          { path: paths.order.checkout, element: <OrderCheckoutPage /> },
        ],
      },
      {
        path: paths.auth.signIn,
        element: (
          <OAuthGuard>
            <GuestGuard>
              <AuthLayout withoutVector />
            </GuestGuard>
          </OAuthGuard>
        ),
        children: [{ index: true, element: <SignInPage /> }],
      },
      {
        path: paths.auth.signUp,
        element: (
          <GuestGuard>
            <AuthLayout />
          </GuestGuard>
        ),
        children: [{ index: true, element: <SignUpPage /> }],
      },
      {
        path: paths.auth.forgotPassword,
        element: (
          <GuestGuard>
            <AuthLayout withoutVector centered />
          </GuestGuard>
        ),
        children: [{ index: true, element: <ForgotPasswordPage /> }],
      },
      {
        path: paths.auth.resetPassword,
        element: (
          <GuestGuard>
            <AuthLayout withoutVector centered />
          </GuestGuard>
        ),
        children: [
          { index: true, element: <Navigate replace to={paths.auth.signIn} /> },
          { path: ':code', element: <ResetPasswordPage /> },
        ],
      },
      {
        path: paths.auth.mfa,
        element: (
          <GuestGuard>
            <AuthLayout withoutVector centered />
          </GuestGuard>
        ),
        children: [{ index: true, element: <MultiFactorAuthPage /> }],
      },
      {
        path: paths.payments.callback,
        element: (
          <AuthGuard>
            <PaymentsCallbackPage />
          </AuthGuard>
        ),
      },
      {
        path: paths.featureRequest.index,
        element: (
          <AuthGuard>
            <CannyProvider>
              <BaseLayout>
                <FeatureRequestPage />
              </BaseLayout>
            </CannyProvider>
          </AuthGuard>
        ),
      },
      {
        path: `${paths.proxies.index}/:proxyId/credentials-generator`,
        element: (
          <AuthGuard>
            <CredentialsGeneratorGuard>
              <BaseLayout>
                <CredentialsGeneratorPage />
              </BaseLayout>
            </CredentialsGeneratorGuard>
          </AuthGuard>
        ),
      },
      {
        path: `${paths.proxies.operation.index}/:operationId`,
        element: (
          <AuthGuard>
            <BaseLayout>
              <OperationPage />
            </BaseLayout>
          </AuthGuard>
        ),
      },
      {
        path: paths.proxies.replace,
        element: (
          <AuthGuard>
            <ReplaceableProxiesGuard>
              <BaseLayout>
                <ReplaceProxiesPage />
              </BaseLayout>
            </ReplaceableProxiesGuard>
          </AuthGuard>
        ),
      },
      {
        path: paths.referrals.bank,
        element: (
          <AuthGuard>
            <BaseLayout>
              <BankDetailsPage />
            </BaseLayout>
          </AuthGuard>
        ),
      },
      {
        path: paths.notFound,
        element: <AuthLayout withoutVector />,
        children: [{ index: true, element: <NotFoundPage /> }],
      },
      { path: '*', element: <Navigate to={paths.notFound} /> },
    ],
  },
]);
